/**
 * Incident follow-ups (13.2-06; D-13.2-A-4, D-13.2-P-7): Action Register rows linked to an incident, so Oversight
 * sees on Verify what is done and what is scheduled.
 *
 * - `buildFollowUp(incident, actor, input, now)` builds the AR create: the fields (SharePoint internal names, the
 *   Person column as `OwnerLookupId`) and the AR audit row. A person wrote it, so it is released at create
 *   (`ReleasedAt`, `ReleasedBy`); the release sitting exists for agent-made rows. `Accepted` when the owner is the
 *   creator, else `Proposed`.
 * - `canAddFollowUp(row, actor)`: CareManager, Oversight or HRWHS, on an open Incident.
 * - `followUpsOf(ref, arRows, now)`: the rows linked to one incident, Retired and NotMine left out, each with a state.
 *
 * **The link convention** (the 25 Oct 2026 GET-only probe of the live AR, recorded in 13.2-06-SUMMARY): new rows
 * carry `SourceRef = ParentId = INC-<id>` with `Source = ParentType = Incident`. On the live list `ParentId` is
 * always `INC-<n>`, and `SourceRef` is `INC-<n>` or `INC-<n>` followed by `;` and free text. `followUpsOf` reads:
 *   1. a row only when `Source = Incident` or `ParentType = Incident`;
 *   2. its `ParentId` when that names an incident (`INC-<n>` exactly, or a bare `<n>`), which then wins;
 *   3. otherwise the leading `INC-<n>` (or bare `<n>`) token of `SourceRef`, ended by `;`, a space or the end.
 * A multi-id `SourceRef` (`INC-591/593; …`, `INC-544/574/… series; …`) is never fanned out to every id it lists: it
 * links only through its `ParentId` (D-13.2-06-2, the conservative reading).
 *
 * **The AR audit row** is the house "created" row the provisioning scripts write (`ACT-<id> created`, a `StateChange`
 * on `Status` from blank). Its id is unknown until the POST, so the builder returns it unstamped and the I/O
 * (13.2-07) stamps it with `stampCreatedAudit`. The Action Register Audit Log has no `ActorUPN` column (a Phase 15
 * CR-H1 prerequisite), so the UPN is in `Note` and `actorLookupId` is sent when it resolved (D-13-00-4).
 *
 * Pure: no Graph, no React, no `Date.now()`.
 */

import { AR_FIELDS, AR_LOOKUP_FIELDS, OWNER_ROLES, actionRef } from '../config/actionRegister';
import { liveChoice } from '../config/fieldset/live';
import type { ActionRow, ActionStatus, Actor, AuditRowDraft, OwnerRole } from '../types/actionRegister';
import type { AppRole } from '../types/auth';
import type { RecordRow } from '../types/records';
import { dueDay } from './briefing';
import { TEXT_CAPS, cleanText, dayNew, toInstant, todayMelbourne } from './fieldValues';

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export type FollowUpKind = 'follow-up' | 'worker-update';

/** `ActionType` per kind, in the column's own kebab-case form (`progress-to-closure`, `safeguarding-followup` …). */
export const FOLLOW_UP_ACTION_TYPES: Readonly<Record<FollowUpKind, string>> = {
  'follow-up': 'incident-follow-up',
  'worker-update': 'incident-worker-update',
};

/** `Tier` / `VerificationMethod` for a person-written, person-done row (D-13.2-06-3). */
export const FOLLOW_UP_TIER = 'human-only' as const;
export const FOLLOW_UP_VERIFICATION = 'self_attestation' as const;

/** The App Roles that may add a follow-up, and the owner role each defaults to (first held wins). */
const ADDER_ROLES: readonly (AppRole & OwnerRole)[] = ['CareManager', 'HRWHS', 'Oversight'];

/** The incident prefix (`registers.ts` composes `INC-<id>`, ISS-008). */
const INCIDENT_PREFIX = 'INC';

export function incidentRef(id: number): string {
  return `${INCIDENT_PREFIX}-${id}`;
}

const [, , , COMPLETED, CANCELLED] = liveChoice('incidents', 'Status', 'New', 'Actioning', 'Evaluating', 'Completed', 'Cancelled');

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FollowUpInput {
  title: string;
  details?: string | null;
  /** Default: the creator's role (CareManager, then HRWHS, then Oversight). */
  ownerRole?: OwnerRole;
  /** Default: the creator. */
  owner?: { upn: string; lookupId: string };
  /** `YYYY-MM-DD`, a Melbourne day, today or later. */
  dueOn: string;
  kind: FollowUpKind;
}

export type FollowUpRefusal =
  | 'not-an-incident'
  | 'incident-closed'
  | 'no-role'
  | 'no-title'
  | 'title-too-long'
  | 'details-too-long'
  | 'bad-due'
  | 'bad-owner-role'
  | 'no-owner';

/** The created row's audit draft before the POST gives it an id (`stampCreatedAudit` completes it). */
export type CreatedAuditDraft = Omit<AuditRowDraft, 'title' | 'parentRef'>;

export type FollowUpBuild =
  | { ok: true; fields: Record<string, string>; audit: CreatedAuditDraft }
  | { ok: false; refusals: FollowUpRefusal[] };

export type FollowUpState = 'done' | 'scheduled' | 'overdue' | 'undated' | 'rejected';

export interface FollowUp {
  row: ActionRow;
  state: FollowUpState;
  /** The due Melbourne day, `YYYY-MM-DD`, or `null`. */
  dueOn: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const lower = (v: unknown): string => text(v).toLowerCase();
const rolesOf = (actor: Actor): readonly AppRole[] => (Array.isArray(actor.roles) ? actor.roles : []);

function isOpenIncident(row: RecordRow): boolean {
  if (row.register !== 'incidents') return false;
  const s = text(row.fields.Status);
  return s !== COMPLETED && s !== CANCELLED;
}

function defaultOwnerRole(actor: Actor): OwnerRole | null {
  const held = rolesOf(actor);
  return ADDER_ROLES.find((r) => held.includes(r)) ?? null;
}

// ---------------------------------------------------------------------------
// canAddFollowUp
// ---------------------------------------------------------------------------

/** CareManager, Oversight or HRWHS, on an Incident that is not Completed or Cancelled. */
export function canAddFollowUp(row: RecordRow, actor: Actor): boolean {
  return isOpenIncident(row) && defaultOwnerRole(actor) !== null;
}

// ---------------------------------------------------------------------------
// buildFollowUp
// ---------------------------------------------------------------------------

const F = AR_FIELDS;

/**
 * The AR create for a follow-up on `incident`, or every refusal at once. `DueDate` is written as the bare Melbourne
 * day (`dayNew`, FIELDSET §1.4: a date-only column; SharePoint stores it as the site's midnight, which every reader
 * takes back as the same day). `DecisionClass` is left blank (an Action), and no agent column is set.
 */
export function buildFollowUp(incident: RecordRow, actor: Actor, input: FollowUpInput, now: Date): FollowUpBuild {
  const refusals: FollowUpRefusal[] = [];

  if (incident.register !== 'incidents') refusals.push('not-an-incident');
  else if (!isOpenIncident(incident)) refusals.push('incident-closed');

  const actorRole = defaultOwnerRole(actor);
  if (actorRole === null) refusals.push('no-role');

  const title = cleanText(input.title, TEXT_CAPS.text);
  if (!title.ok) refusals.push(title.code === 'too-long' ? 'title-too-long' : 'no-title');
  else if (title.value === '') refusals.push('no-title');

  const details = cleanText(input.details ?? '', TEXT_CAPS.textarea);
  if (!details.ok) refusals.push('details-too-long');

  const dueRaw = text(input.dueOn);
  const due = dayNew(dueRaw);
  if (!due.ok || due.value < todayMelbourne(now)) refusals.push('bad-due');

  let ownerRole: OwnerRole | null = actorRole;
  if (input.ownerRole !== undefined) {
    if ((OWNER_ROLES as readonly string[]).includes(input.ownerRole)) ownerRole = input.ownerRole;
    else refusals.push('bad-owner-role');
  }

  const creatorUpn = lower(actor.upn);
  const ownerUpn = input.owner ? lower(input.owner.upn) : creatorUpn;
  const ownerLookupId = input.owner ? text(input.owner.lookupId) : text(actor.lookupId);
  if (ownerLookupId === '') refusals.push('no-owner');

  if (refusals.length > 0) return { ok: false, refusals };

  const at = toInstant(now);
  const ref = incidentRef(incident.id);
  const ownedByCreator = ownerUpn === creatorUpn;
  const status: ActionStatus = ownedByCreator ? 'Accepted' : 'Proposed';
  const actionType = FOLLOW_UP_ACTION_TYPES[input.kind];

  const fields: Record<string, string> = {
    [F.title]: title.ok ? title.value : '',
    [F.kind]: 'Action',
    [F.source]: 'Incident',
    [F.parentType]: 'Incident',
    [F.sourceRef]: ref,
    [F.parentId]: ref,
    [F.actionType]: actionType,
  };
  if (details.ok && details.value !== '') fields[F.description] = details.value;
  fields[F.ownerRole] = ownerRole as OwnerRole;
  fields[AR_LOOKUP_FIELDS.owner] = ownerLookupId;
  fields[F.dueDate] = due.ok ? due.value : '';
  fields[F.executor] = 'Human';
  fields[F.status] = status;
  if (ownedByCreator) fields[F.acceptedAt] = at;
  fields[F.releasedAt] = at;
  fields[F.releasedBy] = creatorUpn;
  fields[F.tier] = FOLLOW_UP_TIER;
  fields[F.verificationMethod] = FOLLOW_UP_VERIFICATION;

  const audit: CreatedAuditDraft = {
    entryTimestamp: at,
    entryType: 'StateChange',
    fieldChanged: F.status,
    oldValue: null,
    newValue: status,
    note:
      `create by ${creatorUpn}: ${actionType} on ${ref}, owner role ${fields[F.ownerRole]}` +
      `${ownedByCreator ? ' (the creator)' : ''}, due ${fields[F.dueDate]}; released at create`,
  };
  if (text(actor.lookupId)) audit.actorLookupId = text(actor.lookupId);

  return { ok: true, fields, audit };
}

/** The created row's audit draft, completed with the id the POST returned (`ACT-<id> created`). */
export function stampCreatedAudit(draft: CreatedAuditDraft, id: number): AuditRowDraft {
  const parentRef = actionRef(id);
  return { ...draft, title: `${parentRef} created`, parentRef };
}

// ---------------------------------------------------------------------------
// followUpsOf
// ---------------------------------------------------------------------------

/** `INC-<n>` exactly, or a bare `<n>` → n. */
const PARENT_ID_RE = /^(?:INC-)?(\d+)$/i;
/** A leading `INC-<n>` (or bare `<n>`) token, ended by `;`, whitespace or the end. `INC-591/593` does not match. */
const SOURCE_REF_RE = /^(?:INC-)?(\d+)(?=$|[;\s])/i;

function refNumber(ref: string | number): string | null {
  if (typeof ref === 'number') return Number.isInteger(ref) && ref > 0 ? String(ref) : null;
  const m = PARENT_ID_RE.exec(text(ref));
  return m ? String(Number(m[1])) : null;
}

/** The incident number a row links to, or `null` (the convention above). */
export function linkedIncident(row: Pick<ActionRow, 'source' | 'parentType' | 'parentId' | 'sourceRef'>): string | null {
  if (row.source !== 'Incident' && row.parentType !== 'Incident') return null;
  const parent = PARENT_ID_RE.exec(text(row.parentId));
  if (parent) return String(Number(parent[1]));
  const source = SOURCE_REF_RE.exec(text(row.sourceRef));
  return source ? String(Number(source[1])) : null;
}

const DONE: readonly ActionStatus[] = ['Done', 'Verified'];
const LEFT_OUT: readonly ActionStatus[] = ['Retired', 'NotMine'];

function stateOf(row: ActionRow, dueOn: string | null, today: string): FollowUpState {
  if (DONE.includes(row.status)) return 'done';
  if (row.status === 'Rejected') return 'rejected';
  if (dueOn === null) return 'undated';
  return dueOn < today ? 'overdue' : 'scheduled';
}

/**
 * The follow-ups of incident `ref` (`INC-<n>` or the numeric id), Retired and NotMine left out, ordered by due day
 * (undated last) then id. Each gets a state: `done` (Done / Verified), `rejected` (the legacy status), or, while
 * open, `scheduled` (due today or later), `overdue` (due day passed) or `undated`. "Today" is the Melbourne day of
 * `now`; a stored due instant is read as its Melbourne day (`dueDay`).
 */
export function followUpsOf(ref: string | number, arRows: readonly ActionRow[], now: Date): FollowUp[] {
  const n = refNumber(ref);
  if (n === null) return [];
  const today = todayMelbourne(now);
  return arRows
    .filter((r) => !LEFT_OUT.includes(r.status) && linkedIncident(r) === n)
    .map((row) => {
      const dueOn = dueDay(row.dueDate);
      return { row, dueOn, state: stateOf(row, dueOn, today) };
    })
    .sort((a, b) => {
      const da = a.dueOn ?? '9999-12-31';
      const db = b.dueOn ?? '9999-12-31';
      if (da !== db) return da < db ? -1 : 1;
      return a.row.id - b.row.id;
    });
}
